import React from 'react';
import { Settings as SettingsIcon, Download, Upload, AlertCircle, Moon, Sun, Trash2, AlertTriangle, X } from 'lucide-react';
import { collection, getDocs, query, where, writeBatch, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db, auth, deleteUserAccount } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';

export default function UserSettings() {
  const { user } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const [deletePassword, setDeletePassword] = React.useState('');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const navigate = useNavigate();

  const showMessage = (message: string, isError: boolean = false) => {
    if (isError) {
      setError(message);
      setSuccess(null);
    } else {
      setSuccess(message);
      setError(null);
    }
    setTimeout(() => {
      setError(null);
      setSuccess(null);
    }, 5000);
  };

  const handleExportData = async () => {
    if (!user) return;

    try {
      // List of collections to export
      const collections = [
        'events',
        'debts',
        'subscriptions',
        'vacationPlans',
        'budgetBuckets',
        'supportTickets',
        'seenMessages',
        'trophies'
      ];

      const exportData: { [key: string]: any[] } = {};

      for (const collectionName of collections) {
        const collectionRef = collection(db, collectionName);
        const q = query(collectionRef, where('userId', '==', user.uid));
        const snapshot = await getDocs(q);
        
        // Convert Timestamps to ISO strings for better compatibility
        exportData[collectionName] = snapshot.docs.map(doc => {
          const data = doc.data();
          // Convert all date fields to ISO strings
          const processedData = Object.entries(data).reduce((acc, [key, value]) => {
            if (value?.toDate instanceof Function) {
              acc[key] = value.toDate().toISOString();
            } else if (Array.isArray(value)) {
              // Handle arrays of objects that might contain dates
              acc[key] = value.map(item => {
                if (item && typeof item === 'object') {
                  return Object.entries(item).reduce((itemAcc, [itemKey, itemValue]) => {
                    if (itemValue?.toDate instanceof Function) {
                      itemAcc[itemKey] = itemValue.toDate().toISOString();
                    } else {
                      itemAcc[itemKey] = itemValue;
                    }
                    return itemAcc;
                  }, {} as any);
                }
                return item;
              });
            } else {
              acc[key] = value;
            }
            return acc;
          }, {} as any);

          return {
            id: doc.id,
            ...processedData
          };
        });
      }

      const dataStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clearcash_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showMessage('Data exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      showMessage('Failed to export data', true);
    }
  };

  const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!user || !event.target.files?.length) return;

    try {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const importedData = JSON.parse(e.target?.result as string);
          const batch = writeBatch(db);

          // Delete existing data first
          const collections = [
            'events',
            'debts',
            'subscriptions',
            'vacationPlans',
            'budgetBuckets',
            'supportTickets',
            'seenMessages',
            'trophies'
          ];

          for (const collectionName of collections) {
            const collectionRef = collection(db, collectionName);
            const q = query(collectionRef, where('userId', '==', user.uid));
            const snapshot = await getDocs(q);
            snapshot.docs.forEach(doc => {
              batch.delete(doc.ref);
            });
          }

          // Import new data
          for (const [collectionName, items] of Object.entries(importedData)) {
            const collectionRef = collection(db, collectionName);
            (items as any[]).forEach(item => {
              const { id, ...itemData } = item;
              
              // Convert ISO date strings back to Firestore timestamps
              const processedData = Object.entries(itemData).reduce((acc, [key, value]) => {
                if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
                  // Convert ISO date strings to Firestore timestamps
                  acc[key] = new Date(value);
                } else if (Array.isArray(value)) {
                  // Handle arrays of objects that might contain dates
                  acc[key] = value.map(item => {
                    if (item && typeof item === 'object') {
                      return Object.entries(item).reduce((itemAcc, [itemKey, itemValue]) => {
                        if (typeof itemValue === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(itemValue)) {
                          itemAcc[itemKey] = new Date(itemValue);
                        } else {
                          itemAcc[itemKey] = itemValue;
                        }
                        return itemAcc;
                      }, {} as any);
                    }
                    return item;
                  });
                } else {
                  acc[key] = value;
                }
                return acc;
              }, {} as any);

              const docRef = doc(collectionRef, id);
              batch.set(docRef, {
                ...processedData,
                userId: user.uid
              });
            });
          }

          await batch.commit();
          showMessage('Data imported successfully');
          event.target.value = '';
        } catch (error) {
          console.error('Error processing import:', error);
          showMessage('Failed to import data: Invalid file format', true);
        }
      };

      reader.readAsText(file);
    } catch (error) {
      console.error('Error importing data:', error);
      showMessage('Failed to import data', true);
    }
  };

  const handleDeleteAllData = async () => {
    if (!user || !auth.currentUser) return;
    
    setIsDeleting(true);
    try {
      // Delete all user data from collections
      const collections = [
        'events',
        'debts',
        'subscriptions',
        'vacationPlans',
        'budgetBuckets',
        'supportTickets',
        'seenMessages',
        'trophies'
      ];

      const batch = writeBatch(db);

      for (const collectionName of collections) {
        const q = query(
          collection(db, collectionName),
          where('userId', '==', user.uid)
        );
        const snapshot = await getDocs(q);
        snapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
      }

      // Delete user document
      batch.delete(doc(db, 'users', user.uid));

      await batch.commit();

      // Sign out and redirect to landing page
      await auth.signOut();
      navigate('/');
      
    } catch (error) {
      console.error('Error deleting account:', error);
      showMessage('Failed to delete account. Please try again.', true);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600 mx-auto"></div>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Loading...</h3>
          <p className="mt-1 text-sm text-gray-500">Please wait while we load your data.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <SettingsIcon className="w-6 h-6 mr-2" />
            User Settings
          </h2>
        </div>

        {(error || success) && (
          <div className={`mb-6 p-4 rounded-md ${
            error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
          }`}>
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 mr-2" />
              <p>{error || success}</p>
            </div>
          </div>
        )}

        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {/* Theme Settings */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Theme Settings</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700">Dark Mode</p>
                <p className="text-sm text-gray-500">Toggle between light and dark themes</p>
              </div>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5 text-gray-600" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-600" />
                )}
              </button>
            </div>
          </div>

          {/* Data Management */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Data Management</h3>
            <div className="space-y-4">
              <div>
                <button
                  onClick={handleExportData}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Data
                </button>
                <p className="mt-1 text-sm text-gray-500">
                  Download a backup of all your ClearCash data
                </p>
              </div>

              <div>
                <label className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  Import Data
                  <input
                    type="file"
                    accept=".json"
                    onChange={handleImportData}
                    className="hidden"
                  />
                </label>
                <p className="mt-1 text-sm text-gray-500">
                  Restore your data from a backup file (this will replace all existing data)
                </p>
              </div>
            </div>
          </div>

          {/* Account Deletion */}
          <div className="p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Delete Account</h3>
            <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
              <div className="flex">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Warning</h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>Deleting your account will:</p>
                    <ul className="list-disc ml-4 mt-1">
                      <li>Permanently delete all your financial data</li>
                      <li>Remove all calendar events and transactions</li>
                      <li>Delete all debts and savings records</li>
                      <li>Remove all support tickets and preferences</li>
                      <li>This action cannot be undone</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete All Data
            </button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Confirm Account Deletion</h3>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-500">
                  Are you absolutely sure you want to delete your account? This action cannot be undone.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteAllData}
                  disabled={isDeleting}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                >
                  {isDeleting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Account
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}